import React, { useEffect, useState } from 'react';
import { CSVLink } from 'react-csv';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { useAppDispatch } from '../../../../app/hooks';
import { tryToFetchAllTrips } from '../../../../store/admin/trips/adminTripsSlice';
import TripsTable from '../tables/TripsTable';
import Button from '../../../../shared/button';
import Modal from '../../../../shared/modal';
import Error from '../../../../shared/error';

interface IExportModal {
    openModal: boolean;
    data?: any;
    handleCloseModal: () => void;
    onGettingFilters: () => void;
}

const ExportTripsModal = ({
    openModal,
    data,
    handleCloseModal,
    onGettingFilters
}: IExportModal) => {
    const dispatch = useAppDispatch();
    const csvLink = React.useRef() as React.MutableRefObject<any>;
    const [loadingDownloadCSV, setLoadingDownloadCSV] = useState<boolean>(false)
    const [loadingDownloadExcel, setLoadingDownloadExcel] = useState<boolean>(false)
    const [downloadCSV, setDownloadCSV] = useState<any>()
    const [submitError, setSubmitError] = useState<any>();

    const onCloseModal = () => {
        handleCloseModal();
    };

    const exportToExcel = (data: string[][], fileName: string = 'data.xlsx') => {
        const worksheet = XLSX.utils.aoa_to_sheet(data);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');

        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

        saveAs(blob, fileName);
        setLoadingDownloadExcel(false)
    };

    const onDownloadData = async (type: string) => {
        if (type === 'csv') setLoadingDownloadCSV(true)
        if (type === 'excel') setLoadingDownloadExcel(true)
        try {
            setSubmitError(undefined)
            const filters: any = onGettingFilters()

            const formatBody = filters
            const response: any = await dispatch(tryToFetchAllTrips(formatBody)).unwrap()
            const result = [Object.keys(response?.[0]), ...response.map((obj: any) => Object.values(obj))];
            if (type === 'excel') {
                exportToExcel(result, 'Trips-data.xlsx')
            } else {
                setDownloadCSV(result || [])
            }
        } catch (err) {
            setSubmitError(`${err}`)
            setLoadingDownloadCSV(false)
            setLoadingDownloadExcel(false)
        }
    }

    useEffect(() => {
        if (downloadCSV && (loadingDownloadCSV)) {
            csvLink.current.link.click()
        }
        setLoadingDownloadCSV(false)
        setLoadingDownloadExcel(false)
    }, [downloadCSV])

    return (
        <Modal
            open={openModal}
            title='Export Data - Trips'
            headerStyle='border-b-1 py-3 mb-4'
            onClose={onCloseModal}
            contentContainerStyle='!rounded-xl min-w-[95%] md:min-w-[70%] lg:min-w-[60%]'>
            <div className='p-2 max-w-[95vw] md:min-w-[70%] lg:min-w-[60%] min-h-[500px]'>
                <div className='flex flex-col md:flex-row md:justify-between my-3'>
                    <p className='text-primary-light dark:text-primary-darkMode mb-3'>How do you want your data exported?</p>
                    <div className='flex flex-col md:flex-row'>
                        <Button
                            label={loadingDownloadExcel ? 'Preparing data' : 'Download as .xlxs'}
                            className='btn-primary-rounded-less dark:!bg-black md:mr-3 mb-4 md:mb-0'
                            onClickButton={() => !loadingDownloadExcel && onDownloadData('excel')}
                        />
                        <CSVLink
                            ref={csvLink}
                            data={downloadCSV?.filter((item: any, idx: number, arr: any) => { return idx > 0 }) || []}
                            headers={downloadCSV?.[0] || []}
                            className={'none'}
                            target={'_blank'}
                            filename={`Trips-data.csv`}
                        ></CSVLink>
                        <Button
                            label={loadingDownloadCSV ? 'Preparing data' : 'Download as .csv'}
                            className='btn-primary-rounded-less dark:!bg-black md:mr-3'
                            onClickButton={() => !loadingDownloadCSV && onDownloadData('csv')}
                        />
                    </div>
                </div>
                {submitError && <Error text={submitError} />}
                <TripsTable
                    rows={data}
                    hideActions={true}
                />
            </div>
        </Modal>
    );
};

export default ExportTripsModal;